import React, { useMemo, useState, useEffect, useRef, useCallback, useContext } from "react";
import { Box, Typography, Button, CircularProgress, Divider, Grid, Paper, Stack } from "@mui/material";
import { green } from "@mui/material/colors";
import CallIcon from "@mui/icons-material/Call";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ProcessOrderModals from "./ProcessOrderModals/ProcessOrderModals";
import { getNewOrders, initiateExotelCall } from "../../../../service/order/orderService";
import { useParams } from "react-router-dom";
import vegIcon from "../../../../Assets/Veg_symbol.svg.png"
import nonVegIcon from "../../../../Assets/Non_veg_symbol.svg.png"
import eggIcon from "../../../../Assets/Egg.svg"
import { notifyError, notifyApiError } from "../../../../components/ToastContainer/Toast/Toast";
import { OrderCountsContext } from "../../Tabs/InvoiceTabs";
import { useAppSelector } from "../../../../Redux/hooks";
import CallInitiatedModal from "../../../../components/CallInitiatedModal/CallInitiatedModal";
import CopyToClipboardIcon from "../../../../components/Copy/CopyToClipboardIcon";

const getTimeAgo = (dateString: string | Date): string => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) {
        return Math.floor(interval) + " years ago";
    }
    interval = seconds / 2592000;
    if (interval > 1) {
        return Math.floor(interval) + " months ago";
    }
    interval = seconds / 86400;
    if (interval > 1) {
        return Math.floor(interval) + " days ago";
    }
    interval = seconds / 3600;
    if (interval > 1) {
        return Math.floor(interval) + " hours ago";
    }
    interval = seconds / 60;
    if (interval > 1) {
        return Math.floor(interval) + " minutes ago";
    }
    return Math.floor(seconds) + " seconds ago";
};

interface OrderItem {
    id: number;
    product_id: string;
    name: string;
    qty: number;
    price: string;
    original_price?: string;
    discount_type?: string;
    discount_value?: string;
    gst_percentage?: string;
    gst_price?: string;
    total_price?: string;
    product_type?: "veg" | "non-veg" | "egg";
}

interface GroupedOrderItem {
    product_id: string;
    name: string;
    qty: number;
    price: string;
    addons?: { name: string; price: string; qty: number }[];
}

interface OrderData {
    id: string;
    order_code: string;
    time: string;
    fullOrderDate: Date;
    customerName: any;
    customerEmail: any;
    customerPhone: any;
    address: string;
    totalAmount: string;
    paymentStatus: string;
    instruction: any;
    items: OrderItem[];
    store_id: any;
    shippingCharge?: string;
    cgst?: string;
    sgst?: string;
    igst?: string;
    sybTotalAmount?: string;
}

interface OrderBoxProps {
    order: any;
    fetchOrders: () => void;
}

interface ProcessOrderModalsProps {
    acceptModalOpen: boolean;
    handleCloseAcceptModal: () => void;
    cancelModalOpen: boolean;
    handleCloseCancelModal: () => void;
    orderData: OrderData;
}

function parseISODuration(duration: string): number {
    if (!duration || typeof duration !== 'string') return 0;
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 0;
    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);
    return hours * 3600 + minutes * 60 + seconds;
}

const SingleOrderBox: React.FC<OrderBoxProps> = ({ order, fetchOrders }) => {
    const [timeAgoString, setTimeAgoString] = useState("N/A");
    const [acceptModalOpen, setAcceptModalOpen] = useState(false);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [now, setNow] = useState(Date.now());
    const prepDuration = useMemo(() => parseISODuration(order.preparation_time), [order.preparation_time]);
    const orderPlaced = new Date(order.order_date || order.created_at || order.updated_at).getTime();
    const expiryTimestamp = orderPlaced + prepDuration * 1000;
    const remainingSeconds = Math.max(0, Math.floor((expiryTimestamp - now) / 1000));
    const [calling, setCalling] = useState(false);
    const [showCallInitiatedModal, setShowCallInitiatedModal] = useState(false);
    
    // Get profile phone_number from Redux
    const profilePhoneNumber = useAppSelector((state) => state.roleAndPermission?.phone_number || '');

    useEffect(() => {
        if (remainingSeconds > 0) {
            const interval = setInterval(() => setNow(Date.now()), 1000);
            return () => clearInterval(interval);
        }
    }, [expiryTimestamp, remainingSeconds]);

    const isExpired = remainingSeconds === 0;

    const formatCountdown = (secs: number) => {
        const m = Math.floor(secs / 60).toString().padStart(2, '0');
        const s = (secs % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const currentOrder = useMemo<OrderData>(() => {
        const orderDateTime = new Date(order.updated_at || order.order_date);
        const addressParts = [
            order.billing_address?.address1,
            order.billing_address?.address2,
            order.billing_address?.address3,
            order.billing_address?.city,
            order.billing_address?.state,
            order.billing_address?.pincode
        ].filter(Boolean);

        const processedItems: OrderItem[] = (order.order_items || []).map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            name: item.product_name,
            qty: item.quantity,
            price: parseFloat(item.price).toFixed(2), // Ensure price is formatted
            original_price: item.original_price ? parseFloat(item.original_price).toFixed(2) : undefined,
            discount_type: item.discount_type,
            discount_value: item.discount_value ? parseFloat(item.discount_value).toFixed(2) : undefined,
            gst_percentage: item.gst_percentage ? parseFloat(item.gst_percentage).toFixed(2) : undefined,
            gst_price: item.gst_price ? parseFloat(item.gst_price).toFixed(2) : undefined,
            total_price: item.total_price ? parseFloat(item.total_price).toFixed(2) : undefined,
            product_type: item.product_type || undefined,
            buyer_order_item_id: item.buyer_order_item_id ?? null,
            customer_value:
                item.customer_value !== null && item.customer_value !== undefined
                    ? Number(item.customer_value)
                    : null,
        }));

        return {
            id: order.id,
            order_code: order.order_code,
            time: orderDateTime.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }),
            fullOrderDate: orderDateTime,
            customerName: order.customer?.name || 'N/A',
            customerEmail: order.customer?.email || 'N/A',
            customerPhone: order.customer?.phone || 'N/A',
            address: addressParts.join(', '),
            totalAmount: parseFloat(order.total_amount).toFixed(2),
            paymentStatus: order.payment_method === 'online' ? 'Paid' : 'Not Paid',
            instruction: order.instructions || order.instruction || 'No special instructions',
            items: processedItems,
            store_id: order.store_id,
            shippingCharge: order.shipping_charge ? parseFloat(order.shipping_charge).toFixed(2) : undefined,
            cgst: order.cgst ? parseFloat(order.cgst).toFixed(2) : undefined,
            sgst: order.sgst ? parseFloat(order.sgst).toFixed(2) : undefined,
            igst: order.igst ? parseFloat(order.igst).toFixed(2) : undefined,
            preparationTime: order.preparation_time,
            sybTotalAmount: order.sub_total ? parseFloat(order.sub_total).toFixed(2) : undefined,
            partialCancelAllowed: order.partial_cancel_allowed === true,
            grossAmount: Number(order.total_gross_amount || order.total_amount || 0),
        };
    }, [order]);

    useEffect(() => {
        if (currentOrder.fullOrderDate) {
            const updateTimeAgo = () => {
                setTimeAgoString(getTimeAgo(currentOrder.fullOrderDate));
            };

            updateTimeAgo();
            const intervalId = setInterval(updateTimeAgo, 60 * 1000);

            return () => clearInterval(intervalId);
        } else {
            setTimeAgoString("N/A");
        }
    }, [currentOrder.fullOrderDate]);

    const handleOpenAcceptModal = () => setAcceptModalOpen(true);
    const handleCloseAcceptModal = () => setAcceptModalOpen(false);

    const handleOpenCancelModal = () => setCancelModalOpen(true);
    const handleCloseCancelModal = () => setCancelModalOpen(false);

    const groupedDisplayItems = useMemo(() => {
        const baseProductsMap = new Map<string, OrderItem & { addons?: OrderItem[] }>();

        currentOrder.items.forEach(item => {
            if (item.product_id.startsWith('a-')) {
                const baseProductIdMatch = item.product_id.match(/a-(\d+)-(\d+)/);
                if (baseProductIdMatch && baseProductIdMatch[1]) {
                    const baseProductId = baseProductIdMatch[1];
                    const foundBaseProduct = currentOrder.items.find(bp => !bp.product_id.startsWith('a-') && bp.product_id === baseProductId);
                    if (foundBaseProduct) {
                        if (!baseProductsMap.has(foundBaseProduct.product_id)) {
                            baseProductsMap.set(foundBaseProduct.product_id, { ...foundBaseProduct, addons: [] });
                        }
                        baseProductsMap.get(foundBaseProduct.product_id)?.addons?.push(item);
                    } else {
                        baseProductsMap.set(item.id.toString(), item);
                    }
                } else {
                    baseProductsMap.set(item.id.toString(), item);
                }
            } else {
                if (!baseProductsMap.has(item.product_id)) {
                    baseProductsMap.set(item.product_id, { ...item, addons: [] });
                }
            }
        });

        const finalDisplayItems: (OrderItem & { addons?: OrderItem[] })[] = [];
        const processedAddonIds = new Set<number>();

        baseProductsMap.forEach(item => {
            const displayItem = { ...item };
            displayItem.addons = displayItem.addons?.filter(addon => {
                if (!processedAddonIds.has(addon.id)) {
                    processedAddonIds.add(addon.id);
                    return true;
                }
                return false;
            });
            finalDisplayItems.push(displayItem);
        });

        currentOrder.items.forEach(item => {
            if (!item.product_id.startsWith('a-') && !baseProductsMap.has(item.product_id)) {
                finalDisplayItems.push(item);
            } else if (item.product_id.startsWith('a-') && !processedAddonIds.has(item.id)) {
                finalDisplayItems.push(item);
            }
        });


        return finalDisplayItems;
    }, [currentOrder.items]);

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `@keyframes blinker { 50% { opacity: 0.5; } }`;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    const phone = currentOrder?.customerPhone;

    const handleCallClick = async () => {
        if (!phone || phone === "N/A" || !profilePhoneNumber) {
            notifyError("Phone number not available");
            return;
        }

        if (!currentOrder.order_code) {
            notifyError("Order code not available");
            return;
        }

        setCalling(true);
        try {
            const payload = {
                order_id: currentOrder.order_code,
                from: profilePhoneNumber,
                to: phone,
            };
            const response = await initiateExotelCall(payload);
            if (response?.status === 200 || response?.status === 201) {
                setShowCallInitiatedModal(true);
            } else {
                const errorMessage = response?.data?.message || "Failed to initiate call";
                notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || "Failed to initiate call";
            notifyError(errorMessage === "Internal Server Error" ? "Something went wrong!" : errorMessage);
        } finally {
            setCalling(false);
        }
    };

    return (
        <Paper sx={{ mb: 2, border: "1px solid #ddd", transition: 'border 0.3s', display: "flex", flexDirection: "row" }} >
            <Box
                sx={{
                    bgcolor: "#f9f9f9", flex: "0 0 35%", p: 2, display: "flex", flexDirection: "column",
                    justifyContent: "space-between", borderRight: "1px dashed #ccc", gap: 1.5, borderRadius: 1
                }}
            >
                <Box
                    sx={{
                        height: 300,
                        bgcolor: "#f9f9f9", flex: "0 0 35%", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 1.5
                    }}
                >
                    <Box>
                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Order ID
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {currentOrder.time}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ bgcolor: '#E0F2F1', px: 1.5, py: 0.5, borderRadius: '6px', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                <Typography fontWeight={600} fontSize={12}>
                                    {currentOrder.order_code}
                                </Typography>
                            </Box>
                            <CopyToClipboardIcon value={currentOrder.order_code || ""} title="Copy Order ID" size={16} />
                        </Box>
                    </Box>

                    <Divider />

                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography fontWeight="bold" sx={{ pt: 0.5, pb: 0.5 }}>
                                    {currentOrder.customerName}
                                </Typography>
                            </Box>
                            <Box
                                display="flex"
                                alignItems="center"
                                sx={{
                                    color: green[600],
                                    cursor: phone !== "N/A" && profilePhoneNumber ? "pointer" : "default",
                                    overflow: "hidden",
                                    width: "fit-content",
                                }}
                                onClick={handleCallClick}
                            >
                                <CallIcon 
                                    fontSize="small" 
                                    sx={{ 
                                        mr: 0.5, 
                                        transition: "transform 0.3s",
                                        opacity: calling ? 0.6 : 1,
                                        cursor: phone !== "N/A" && profilePhoneNumber ? "pointer" : "default"
                                    }} 
                                />
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                            <Box sx={{ maxWidth: '70%' }}>
                                <Typography
                                    fontSize={12}
                                    fontWeight={500}
                                    sx={{ display: 'block' }}
                                >
                                    {[
                                        // order.billing_address?.address1,
                                        // order.billing_address?.address2,
                                        order.billing_address?.address3
                                    ].filter(Boolean).join(', ')}
                                </Typography>
                                <Typography
                                    fontSize={12}
                                    fontWeight={500}
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                >
                                    {[
                                        order.billing_address?.city,
                                        order.billing_address?.state,
                                        order.billing_address?.pincode
                                    ].filter(Boolean).join(', ')}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography fontSize={12} fontWeight={500} color="grey" mb={0.5}>
                            Instruction:
                        </Typography>
                        <Typography fontSize={12} fontWeight={500}>
                            {currentOrder.instruction}
                        </Typography>
                    </Box>

                    <Typography variant="caption" fontWeight={500} display={'flex'} justifyContent={'flex-end'}>
                        Placed {timeAgoString}
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    flex: "0 0 65%", p: 2, display: "flex", flexDirection: "column", justifyContent: "space-between",
                    backgroundColor: "white", borderRadius: 1
                }}
            >
                <Stack >
                    {groupedDisplayItems.length > 0 ? (
                        groupedDisplayItems.map((item, index) => (
                            <Box key={item.product_id || index}
                                display="flex" flexDirection="column" alignItems="flex-start" mb={1}
                            >
                                <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                    {/* Left: Icon + Name */}
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {item.product_type === "veg" && (
                                            <img src={vegIcon} alt="Veg" style={{ width: 16, height: 16 }} />
                                        )}
                                        {item.product_type === "non-veg" && (
                                            <img src={nonVegIcon} alt="Non-Veg" style={{ width: 16, height: 16 }} />
                                        )}
                                        {item.product_type === "egg" && (
                                            <img src={eggIcon} alt="Egg" style={{ width: 16, height: 16 }} />
                                        )}
                                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                                            {item.name} x {item.qty}
                                        </Typography>
                                    </Box>

                                    {/* Right: Price */}
                                    <Typography fontWeight={500}>₹{item.total_price}</Typography>
                                </Box>

                                {/* Addons Section */}
                                {item.addons && item.addons.length > 0 && (
                                    <Box mt={0.5} width="100%" sx={{ pl: 2 }}>
                                        {item.addons.map((addon: any, j: number) => {
                                            return (
                                                <Box key={addon.id || j} display="flex" justifyContent="space-between" width="100%" sx={{ pl: 1 }}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: "block", pr: 1 }}
                                                    >
                                                        • {addon.name} x {addon.qty}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ display: "block" }}
                                                    >
                                                        ₹{addon.total_price}
                                                    </Typography>
                                                </Box>
                                            )
                                        })}
                                    </Box>
                                )}
                            </Box>
                        ))
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            No items available
                        </Typography>
                    )}
                </Stack>

                <Divider />

                <Box display="flex" flexDirection="column" alignItems="flex-start" mb={1}>
                    <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                      <Typography variant="body2" fontSize={12} fontWeight={600} color="grey">  Sub Total: </Typography>
                      <Typography variant="body2" fontSize={12} fontWeight={600} color="grey">
                        ₹{currentOrder.sybTotalAmount}
                      </Typography>
                    </Box>
                </Box>

                <Divider />

                <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", p: 2, mt: 2 }}>
                    <Grid container>
                        {/* Left side labels */}
                        <Grid item xs={9}>
                            {/* {currentOrder?.shippingCharge && Number(currentOrder?.shippingCharge) !== 0 && <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                Delivery Charge
                            </Typography>} */}

                            {Number(currentOrder?.cgst) !== 0 ? (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        CGST
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        SGST
                                    </Typography>
                                </>
                            ) : Number(currentOrder?.igst) !== 0 ? (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        IGST
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        SGST
                                    </Typography>
                                </>
                            ) : null}
                        </Grid>

                        {/* Right side values */}
                        <Grid item xs={3} textAlign="right">
                            {/* {currentOrder?.shippingCharge && Number(currentOrder?.shippingCharge) !== 0 && <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                ₹{Number(currentOrder?.shippingCharge || 0).toFixed(2)}
                            </Typography>} */}

                            {Number(currentOrder?.cgst) !== 0 ? (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        ₹{Number(currentOrder?.cgst || 0).toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        ₹{Number(currentOrder?.sgst || 0).toFixed(2)}
                                    </Typography>
                                </>
                            ) : Number(currentOrder?.igst) !== 0 ? (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        ₹{Number(currentOrder?.igst || 0).toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                                        ₹{Number(currentOrder?.sgst || 0).toFixed(2)}
                                    </Typography>
                                </>
                            ) : null}
                        </Grid>
                    </Grid>

                    {/* Divider */}
                    <Box sx={{ borderTop: "1px dashed #e0e0e0", my: 1 }} />

                    {/* Total row */}
                    <Grid container>
                        <Grid item xs={9} display="flex" alignItems="center">
                            <Typography fontWeight="bold" mr={1}>
                                Total
                            </Typography>
                            <Box
                                sx={{
                                    bgcolor: currentOrder.paymentStatus === "Paid" ? "green" : "orange",
                                    color: "white",
                                    fontSize: "0.7rem",
                                    px: 1,
                                    py: 0.2,
                                    borderRadius: "6px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                }}
                            >
                                {currentOrder.paymentStatus}
                            </Box>
                        </Grid>
                        <Grid item xs={3} textAlign="right">
                            <Typography fontWeight={600}>₹{currentOrder.totalAmount}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                <Stack direction="row" spacing={2} mt={2}>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleOpenCancelModal}
                        sx={{
                            bgcolor: 'white',
                            textTransform: "none",
                            fontWeight: 500,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleOpenAcceptModal}
                        sx={{
                            bgcolor: "black",
                            textTransform: "none",
                            fontWeight: 500,
                            "&:hover": { bgcolor: "#333" },
                        }}
                    >
                        Accept Order
                    </Button>
                </Stack>
            </Box>
            <ProcessOrderModals
                acceptModalOpen={acceptModalOpen}
                handleCloseAcceptModal={handleCloseAcceptModal}
                cancelModalOpen={cancelModalOpen}
                handleCloseCancelModal={handleCloseCancelModal}
                orderData={currentOrder as any}
                fetchOrderData={fetchOrders}
            />
            <CallInitiatedModal
                open={showCallInitiatedModal}
                onClose={() => setShowCallInitiatedModal(false)}
                phoneNumber={profilePhoneNumber}
            />
        </Paper>
    );
};

const New: React.FC = () => {
    const [orderData, setOrderData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [fetchingMore, setFetchingMore] = useState(false);
    const { store_id } = useParams();


    // Get the context for sharing counts with safe fallback
    const orderCountsContext = useContext(OrderCountsContext);
    const { updateCount, countsLoaded, refreshCallbacksRef } = orderCountsContext || {
        updateCount: () => { },
        countsLoaded: false,
        refreshCallbacksRef: { current: {} as { newOrder?: () => void; preparing?: () => void; ready?: () => void } }
    };

    const fetchOrders = useCallback(async (pageToFetch = 1, append = false, showLoading = true) => {
        if (pageToFetch > totalPages) return;
        if (pageToFetch === 1 && showLoading) setLoading(true);
        else if (pageToFetch > 1) setFetchingMore(true);
        setError(null);
        try {
            const res = await getNewOrders(pageToFetch, store_id as any, "pending", showLoading);
            const data = res?.data?.data;
            const orders = data?.orders || data?.invoice || data?.data || [];
            setTotalPages(data?.totalPages || 1);
            const newTotalCount = data?.total || orders.length;
            setTotalCount(newTotalCount);
            // Update the context with the new count (only if counts haven't been loaded yet)
            if (!countsLoaded) {
                updateCount('new', newTotalCount);
            }
            if (append) {
                setOrderData(prev => [...prev, ...(Array.isArray(orders) ? orders : [])]);
            } else {
                setOrderData(Array.isArray(orders) ? orders : []);
            }
        } catch (e: any) {
            // `e.response` is undefined on every network drop and timeout, so
            // reading `.data` off it threw inside this catch — the toast never
            // appeared and the failure surfaced as an unhandled rejection.
            notifyApiError(e, "new orders", () => {
                void fetchOrders(pageToFetch, append, showLoading);
            });
            // Keep rows already on screen when a refresh fails; only clear when
            // there was nothing loaded to begin with.
            if (!append) setOrderData((prev) => (prev.length > 0 ? prev : []));
        } finally {
            if (pageToFetch === 1 && showLoading) setLoading(false);
            else if (pageToFetch > 1) setFetchingMore(false);
        }
    }, [store_id, totalPages]);

    useEffect(() => {
        setPage(1);
        fetchOrders(1, false);
    }, [store_id, fetchOrders]);

    // Register refresh callback with parent component for auto-refresh polling
    useEffect(() => {
        if (refreshCallbacksRef && refreshCallbacksRef.current) {
            refreshCallbacksRef.current.newOrder = () => {
                fetchOrders(1, false, false); // showLoading = false
            };
        }

        return () => {
            if (refreshCallbacksRef && refreshCallbacksRef.current) {
                delete refreshCallbacksRef.current.newOrder;
            }
        };
    }, [fetchOrders, refreshCallbacksRef]);



    const pendingOrders = orderData.filter(order => order.status === "pending");

    return (
        <Box>
            {loading ? (
                <Typography>Loading...</Typography>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : pendingOrders.length > 0 ? (
                <>
                    {pendingOrders.map((order, index) => (
                        <SingleOrderBox key={order.id || index} order={order} fetchOrders={fetchOrders} />
                    ))}
                    {page < totalPages && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={fetchingMore ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}
                                disabled={fetchingMore}
                                onClick={() => {
                                    const nextPage = page + 1;
                                    setPage(nextPage);
                                    fetchOrders(nextPage, true, false);
                                }}
                                sx={{ textTransform: 'none' }}
                            >
                                {fetchingMore ? 'Loading...' : 'View More'}
                            </Button>
                        </Box>
                    )}
                </>
            ) : (
                <Box
                    sx={{
                        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                        height: 'auto', p: 2,
                    }}
                >
                    <img
                        src=""
                        alt=""
                        style={{ maxWidth: '100%', height: 'auto', display: 'block', marginBottom: '16px' }}
                    />
                    <Typography variant="body1" color="text.secondary">
                        Waiting for new adventures...
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export default New;